import { useCallback, useRef } from 'react';

// Keydown-only signal for "this field was actually typed into," as opposed to
// filled by a password manager: autofill (browser-native or extension-based)
// sets the input's value programmatically and never dispatches native key
// events, so any keydown here means a human hit a key. Navigation/modifier
// keys are excluded since they don't enter or edit content.
const NON_ENTRY_KEYS = new Set([
  'Tab',
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'CapsLock',
  'Escape',
  'Enter',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Insert',
  'ContextMenu',
  'NumLock',
  'ScrollLock',
  'PrintScreen',
]);

export const useManualEntrySignal = () => {
  const manualEntryRef = useRef(false);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (NON_ENTRY_KEYS.has(e.key)) {
      return;
    }
    manualEntryRef.current = true;
  }, []);

  // Forgets any prior manual entry each time the field regains focus, so a
  // failed submit followed by a retry via autofill alone (no keydown before
  // the next submit) doesn't keep reporting true from the earlier attempt.
  const onFocus = useCallback(() => {
    manualEntryRef.current = false;
  }, []);

  return { manualEntryRef, onKeyDown, onFocus };
};
